/**
 * PATH: packages/auth/src/session-manager/tokenSync.ts
 * FILE_ID: AUTH-XTAB-SYNC-001
 * PURPOSE: Cross-tab synchronisation primitives for auth events
 *          (logout, token refresh, identity change). All signals
 *          flow via the `storage` event on localStorage, which only
 *          fires in OTHER tabs of the same origin — perfect for
 *          "tell every sibling tab my identity just changed".
 * WHY:     Cookies are scoped to `.quotech.ai` so a login in one tab
 *          silently swaps the cookie jar every other tab reads from.
 *          Without an explicit cross-tab broadcast the sibling tabs
 *          keep painting the old user's data while sending requests
 *          under the new user's bearer — a cross-tenant data leak
 *          shape (see BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT).
 * FLOW:
 *   ┌────────────────┐    storage      ┌────────────────┐
 *   │ Tab B (login)  │ ─── event ────► │ Tab A (idle)   │
 *   │ broadcast(uid) │                 │ listener clears │
 *   └────────────────┘                 │ caches + reload │
 *                                       └────────────────┘
 * DEPENDENCIES:
 *   - ./constants: shared keys + JWT shape check.
 * RELATED:
 *   - useIdentityPoll.ts (packages/ui): periodic backstop; this file
 *     is the immediate-signal layer that closes its ~60s window.
 *   - sessionHygiene.ts (packages/ui): the wipe-on-user-change utility
 *     the receiver calls when it confirms a real identity change.
 */

import { LOGOUT_EVENT_KEY, TOKEN_SYNC_KEY, isValidJWTStructure } from './constants'

/** Cross-tab identity-change broadcast key. Other tabs subscribe via
 *  `createIdentityChangeListener`. The value is JSON-encoded to carry
 *  the new identity payload (user id + org id + a wall-clock stamp
 *  used for stale-event filtering). */
export const IDENTITY_CHANGE_EVENT_KEY = 'quotech:identity_changed'

/** Maximum age, in ms, of a cross-tab identity broadcast we are
 *  willing to act on. Anything older was already handled (or was
 *  observed by another tab and reloaded). 60s is generous enough to
 *  absorb tab-was-suspended-by-OS naps without being so long that a
 *  truly stale event from a previous session could fire on focus. */
export const IDENTITY_CHANGE_MAX_AGE_MS = 60_000

/** Shape of the JSON payload written to `quotech:identity_changed`. */
export interface IdentityChangePayload {
  /** The new authenticated user id (master `sub` / `user_id` /
   *  email). `null` means "we cannot identify the new user" and the
   *  receiver should treat it as a worst-case identity change. */
  userId: string | number | null
  /** The new organisation id (master `quotech_org` cookie value or
   *  the active tenant org id). Optional — used for invalidating
   *  org-keyed caches. */
  orgId: string | number | null
  /** Wall-clock timestamp (Date.now()) at broadcast time. Used by
   *  receivers to drop stale events that fire on tab restoration. */
  t: number
  /** Human-readable diagnostic tag (e.g. "master signin", "tenant SSO
   *  applySession", "useAuthStore.setAuth"). Surfaces in logs so we
   *  can trace which mutation site fired the broadcast. */
  reason?: string
}

/** Sync token to localStorage keys that API clients read */
export function syncTokenToLocalStorage(token: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem('IdToken', token)
    localStorage.setItem('accessTokennew', token)
  } catch (error) {
    console.warn('[@quotech/auth] Failed to sync token to localStorage:', error)
  }
}

/** Clear auth-related localStorage keys.
 *
 * DURABILITY (BUG-2026-06-30-STALE-TOKEN-RESURRECTION): also drop the
 * persisted `quotech-auth` Zustand store. On session death/logout the
 * legacy bearer keys were cleared but the Zustand persist blob (which
 * carries its own `accessToken` + `user`) survived — a half-state that
 * re-seeds a stale identity on the next boot. Removing it means a dead
 * session reboots into a clean logged-out state instead of a poisoned
 * jar. The shared `.quotech.ai` cookies are cleared separately by
 * `clearAllAuthCookies`. */
export function clearLocalStorageAuth(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem('IdToken')
    localStorage.removeItem('accessTokennew')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('UserData')
    localStorage.removeItem('quotech-auth')
  } catch { /* ignore */ }
}

/** Broadcast token to other tabs via localStorage storage event */
export function broadcastTokenSync(token: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(TOKEN_SYNC_KEY, token)
  } catch { /* ignore */ }
}

/** Broadcast logout to other tabs via localStorage storage event */
export function broadcastLogout(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString())
    localStorage.removeItem(LOGOUT_EVENT_KEY)
  } catch { /* ignore */ }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('quotech:logout'))
  }
}

/** Create a storage event handler for cross-tab token sync */
export function createTokenSyncListener(onSync: (token: string) => void): (e: StorageEvent) => void {
  return (e: StorageEvent) => {
    if (e.key === TOKEN_SYNC_KEY && e.newValue && isValidJWTStructure(e.newValue)) {
      console.log('[@quotech/auth] Token synced from another tab')
      onSync(e.newValue)
    }
  }
}

/** Create a storage event handler for cross-tab logout detection */
export function createLogoutListener(onLogout: () => void): (e: StorageEvent) => void {
  return (e: StorageEvent) => {
    if (e.key === LOGOUT_EVENT_KEY && e.newValue) {
      console.log('[@quotech/auth] Logout detected from another tab')
      onLogout()
    }
  }
}

/** Broadcast an identity change to every other tab on the same origin.
 *
 *  This MUST be called from EVERY mutation path that writes a new
 *  authenticated identity into the cookie jar or the auth store. The
 *  set of mutation sites is enumerated in
 *  `RECURRENCE_LOG.md::REC-2026-05-21-CROSS-TAB-IDENTITY-DRIFT` so
 *  that future agents adding a new login path can find the list and
 *  wire the broadcast there.
 *
 *  Same-tab listeners do NOT receive the resulting `storage` event —
 *  that's by design; the writing tab already knows it just logged in.
 *  We immediately `removeItem` after `setItem` so a future identical
 *  payload still fires the event (storage events fire on every change,
 *  but only when oldValue !== newValue).
 */
export function broadcastIdentityChange(payload: Omit<IdentityChangePayload, 't'>): void {
  if (typeof localStorage === 'undefined') return
  // HOTFIX-FOLLOW-UP (2026-05-21 evening): drop broadcasts with a
  // null/empty userId. The receiver's same-user check used to fall
  // through to `window.location.reload()` when either side was null,
  // which created a multi-tab feedback loop ("the screen is
  // refreshing across the app"). Even though every receiver is now
  // hardened to require a non-null incoming id before reloading,
  // sending null tells siblings nothing actionable — it's pure noise
  // that pollutes their console and indicates a caller bug (a code
  // path that couldn't resolve the identity should not be claiming
  // an identity change). Silently drop it here so the bug surfaces
  // as "no broadcast received" instead of "every sibling reloads".
  // See BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT.
  if (payload.userId == null) return
  if (typeof payload.userId === 'string' && payload.userId.trim() === '') return
  const full: IdentityChangePayload = { ...payload, t: Date.now() }
  try {
    localStorage.setItem(IDENTITY_CHANGE_EVENT_KEY, JSON.stringify(full))
    localStorage.removeItem(IDENTITY_CHANGE_EVENT_KEY)
  } catch { /* ignore — broadcast must never throw into the login path */ }
}

/** Create a storage event handler for cross-tab identity-change
 *  detection. Pass `onChange` a function that compares the broadcast
 *  payload to the tab's "what user I think I am" pin and, on a real
 *  change, clears caches + reloads / re-bootstraps.
 *
 *  Receivers are expected to be wired in EVERY SPA's main entry
 *  (master `src/main.jsx`, tenant `apps/<tenant>/src/main.tsx`).
 *  See RECURRENCE_LOG.md for the receiver-side guidance.
 */
export function createIdentityChangeListener(
  onChange: (payload: IdentityChangePayload) => void,
): (e: StorageEvent) => void {
  return (e: StorageEvent) => {
    if (e.key !== IDENTITY_CHANGE_EVENT_KEY) return
    if (!e.newValue) return
    let parsed: IdentityChangePayload | null = null
    try {
      const json = JSON.parse(e.newValue) as IdentityChangePayload
      if (!json || typeof json !== 'object') return
      parsed = json
    } catch { return }
    if (!parsed) return
    if (typeof parsed.t !== 'number') return
    if (Date.now() - parsed.t > IDENTITY_CHANGE_MAX_AGE_MS) {
      console.log('[@quotech/auth] Stale identity-change event ignored (age > 60s)')
      return
    }
    onChange(parsed)
  }
}

/** Tiny JWT-payload `sub` extractor used by the silent-refresh
 *  identity guard. Compares the OLD access token's `sub` to the NEW
 *  one after a refresh; a mismatch means the cookie jar was swapped
 *  by a sibling tab between the request and the refresh response —
 *  abort the swap rather than write a different user's token into
 *  this tab's storage.
 *
 *  Returns null on any decode failure (callers must treat null vs
 *  null as "indeterminate — let the refresh through" to avoid breaking
 *  legitimate first-refresh paths where the cached old token may be
 *  null). Returns a stringified value for stable equality compares.
 *
 *  Kept here (rather than reusing identityBinding.ts in @quotech/ui)
 *  because @quotech/auth deliberately does not depend on @quotech/ui.
 */
export function decodeJwtSub(token: string | null | undefined): string | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const middle = parts[1]
  if (!middle) return null
  try {
    const padded = middle.replace(/-/g, '+').replace(/_/g, '/')
    const pad = padded.length % 4
    const b64 = pad ? padded + '='.repeat(4 - pad) : padded
    // Browser-native atob only (vitest jsdom and Node 18+ both provide it).
    // Avoids requiring @types/node in tenant SPAs that compile @quotech/auth source.
    if (typeof atob === 'undefined') return null
    const decoded = atob(b64)
    const json = JSON.parse(decoded) as Record<string, unknown>
    if (!json || typeof json !== 'object') return null
    const sub = json.sub
    if (sub === null || sub === undefined) return null
    return String(sub).trim().toLowerCase()
  } catch {
    return null
  }
}

/** PURE decision: does the cookie jar belong to a DIFFERENT account than the
 *  live bearer? Given the live bearer's owning user id and the jar's
 *  access/refresh owning user ids (all from {@link decodeJwtUserId}), the jar
 *  is "stale" iff either cookie carries a DEFINITE id that disagrees with the
 *  bearer. Indeterminate ids (null — no live bearer, or an undecodable
 *  cookie) NEVER count as stale, so the guard can only fire on a genuine
 *  cross-account mismatch and never on a legitimate same-user session.
 *
 *  Exported (no side effects) so the SessionManager's clear-and-reseal branch
 *  is unit-testable without touching document.cookie / localStorage.
 *  BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT. */
export function isStaleCookieJar(args: {
  bearerId: string | null
  accessId: string | null
  refreshId: string | null
}): boolean {
  const { bearerId, accessId, refreshId } = args
  if (!bearerId) return false
  const staleAccess = accessId !== null && accessId !== bearerId
  const staleRefresh = refreshId !== null && refreshId !== bearerId
  return staleAccess || staleRefresh
}

/** Extract the OWNING USER of a Quotech JWT for cross-identity comparison.
 *
 *  WHY (BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT): Quotech SimpleJWT access &
 *  refresh tokens key identity on the `user_id` claim (the Django pk —
 *  `SIMPLE_JWT.USER_ID_CLAIM`), NOT the standard `sub`. `decodeJwtSub`
 *  therefore returns null for them, so it cannot tell a prior account's
 *  cookie apart from the current one. This reads `user_id` first (with
 *  `sub`/`userId` fallbacks) so the SessionManager can detect a cookie jar
 *  left behind by a DIFFERENT account after an account switch and stop it
 *  from forcing a logout of the freshly-authenticated user.
 *
 *  Returns a stable lowercased string for equality compares, or null on any
 *  decode failure / missing claim (callers treat null as "indeterminate —
 *  do not act", so a structurally-odd token never triggers a false clear).
 */
export function decodeJwtUserId(token: string | null | undefined): string | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const middle = parts[1]
  if (!middle) return null
  try {
    const padded = middle.replace(/-/g, '+').replace(/_/g, '/')
    const pad = padded.length % 4
    const b64 = pad ? padded + '='.repeat(4 - pad) : padded
    if (typeof atob === 'undefined') return null
    const json = JSON.parse(atob(b64)) as Record<string, unknown>
    if (!json || typeof json !== 'object') return null
    const id = json.user_id ?? json.sub ?? json.userId
    if (id === null || id === undefined) return null
    const s = String(id).trim().toLowerCase()
    return s ? s : null
  } catch {
    return null
  }
}
